import { useEffect, useRef, useState } from "react";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { CogsGap, CogsGapMatch } from "@/lib/manufacturing/cogsCatalogView";

const OPTIONS: { id: CogsGap; label: string }[] = [
  { id: "materials", label: "Missing materials" },
  { id: "labor", label: "Missing labor" },
  { id: "other", label: "Other gaps" },
];

type Props = {
  gaps: readonly CogsGap[];
  match: CogsGapMatch;
  disabled?: boolean;
  onGaps: (gaps: CogsGap[]) => void;
  onMatch: (match: CogsGapMatch) => void;
};

function summary(gaps: readonly CogsGap[], match: CogsGapMatch): string {
  if (gaps.length === 0) return "Any completeness";
  const labels = OPTIONS.filter((option) => gaps.includes(option.id)).map((option) => option.label);
  return labels.join(match === "or" ? " or " : " and ");
}

export function ManufacturingPricingGapFilter({ gaps, match, disabled, onGaps, onMatch }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle(id: CogsGap) {
    onGaps(gaps.includes(id) ? gaps.filter((gap) => gap !== id) : [...gaps, id]);
  }

  return (
    <div ref={rootRef} className="relative block min-w-[10rem] flex-1 space-y-0.5 text-sm sm:max-w-[16rem]">
      <span className="text-xs text-gray-600 dark:text-zinc-400">Missing</span>
      <button
        type="button"
        className={`${inputClass} flex w-full items-center justify-between gap-2 text-left`}
        onClick={() => setOpen((value) => !value)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{summary(gaps, match)}</span>
        <span className="text-[10px] text-gray-400">▾</span>
      </button>
      {open ? (
        <div className="absolute z-30 mt-1 w-full min-w-[16rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-950">
          <p className="px-3 py-1.5 text-[11px] leading-snug text-gray-500 dark:text-zinc-400">
            Materials have no unit cost. Labor rates are missing Pays by or a size. Other gaps are an unsaved, failed, or stale materials total, or a part with nothing on hand.
          </p>
          <ul role="listbox" aria-multiselectable className="text-sm">
            {OPTIONS.map((option) => (
              <li key={option.id}>
                <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-zinc-900">
                  <input
                    type="checkbox"
                    checked={gaps.includes(option.id)}
                    onChange={() => toggle(option.id)}
                    disabled={disabled}
                  />
                  <span>{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mx-3 my-1 flex rounded-md border border-gray-200 p-0.5 text-xs dark:border-zinc-700">
            <button
              type="button"
              className={`flex-1 rounded px-2 py-1 ${match === "and" ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "text-gray-600 dark:text-zinc-300"}`}
              aria-pressed={match === "and"}
              onClick={() => onMatch("and")}
            >
              And
            </button>
            <button
              type="button"
              className={`flex-1 rounded px-2 py-1 ${match === "or" ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "text-gray-600 dark:text-zinc-300"}`}
              aria-pressed={match === "or"}
              onClick={() => onMatch("or")}
            >
              Or
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
